import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import FormInput from '../../ui/FormInput'
import CountriesDropdown from './SignupItems/CountriesDropdown'
import FormCheckbox from './SignupItems/FormCheckbox'
import useNotification from '../../../hooks/useNotification'

/**
 * Step3PersonalInfo Component
 *
 * Third step of signup: User completes registration with personal info
 * Form adapts based on user_type (professional vs learner)
 */
const Step3PersonalInfo = ({ userType, selectedModules, onSubmit, onBack }) => {
  const { t } = useTranslation('auth')
  const [accept1, setAccept1] = useState(false)
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      companyName: '',
      vatNumber: '',
      description: '',
      selectedCountry: { text: 'Select country *', image: null, code: null },
    },
  })

  const { error: showError } = useNotification()

  // Ragione sociale e partita IVA: facoltative (decisione 06/10/2026, il SaaS si
  // vende anche a singoli individui). Si mostrano solo ai profili professionali.
  const isProfessional = userType !== 'learner'

  const handleFormSubmit = async (data) => {
    if (!accept1) {
      setIsFormSubmitted(true)
      showError(t('signup.toast.acceptDisclaimer'))
      return
    }

    if (!getValues('selectedCountry').code) {
      setIsFormSubmitted(true)
      showError(t('signup.toast.selectCountry'))
      return
    }

    // Prepare signup data
    const signupData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      password: data.password,
      companyName: isProfessional ? data.companyName : '',
      vatNumber: isProfessional ? data.vatNumber : '',
      country: data.selectedCountry.text,
      countryCode: data.selectedCountry.code,
      description: data.description || 'Not specified',
      user_type: userType,
      enabled_modules: selectedModules,
      // GDPR Art. 7: the backend rejects signup (400) without explicit consent.
      // accept1 is guaranteed true here (gated above), but send it + a timestamp
      // so the consent is actually recorded.
      consent_accepted: accept1,
      consent_timestamp: new Date().toISOString(),
    }

    // Call parent onSubmit
    onSubmit(signupData)
  }

  return (
    <div className="max-w-2xl mx-auto flex-1 flex flex-col min-h-0">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col flex-1 min-h-0 gap-2">
        <div className="flex-shrink-0 space-y-2">
        {/* Basic Info - Always shown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <FormInput
            label={t('labels.firstName')}
            name="firstName"
            type="text"
            register={register}
            validationRules={{ required: t('validation.firstNameRequired') }}
            errors={errors}
            required={true}
          />
          <FormInput
            label={t('labels.lastName')}
            name="lastName"
            type="text"
            register={register}
            validationRules={{ required: t('validation.lastNameRequired') }}
            errors={errors}
            required={true}
          />
        </div>

        <FormInput
          label={t('labels.email')}
          type="email"
          name="email"
          register={register}
          validationRules={{
            required: t('validation.emailRequired'),
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: t('validation.emailInvalid'),
            },
          }}
          errors={errors}
          required={true}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <FormInput
            label={t('labels.password')}
            type="password"
            name="password"
            register={register}
            validationRules={{
              required: t('validation.passwordRequired'),
              // Must mirror the backend (validate_password_strength): 12+ chars
              // with upper/lower/digit/special, else signup 400s silently.
              minLength: {
                value: 12,
                message: t('validation.passwordMinLength'),
              },
              validate: {
                complexity: (value) => {
                  const special = "!@#$%^&*(),.?\":{}|<>_-+=[]\\/~`"
                  const ok =
                    /[A-Z]/.test(value) &&
                    /[a-z]/.test(value) &&
                    /[0-9]/.test(value) &&
                    [...value].some((c) => special.includes(c))
                  return ok || t('validation.passwordComplexity')
                },
              },
            }}
            errors={errors}
            required={true}
            withPasswordToggle={true}
          />
          <FormInput
            label={t('labels.confirmPassword')}
            type="password"
            name="confirmPassword"
            register={register}
            validationRules={{
              required: t('validation.confirmPasswordRequired'),
              validate: (value) =>
                value === getValues('password') ||
                t('validation.passwordsNotMatch'),
            }}
            errors={errors}
            required={true}
            withPasswordToggle={true}
          />
        </div>

        {/* Professional Fields - Conditional */}
        {isProfessional && (
          <>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
              <h3 className="text-lg font-semibold mb-2 text-white">
                {t('signup.step3.professionalInfoHeading')}
              </h3>
            </div>

            <FormInput
              label={t('labels.companyName')}
              name="companyName"
              type="text"
              register={register}
              errors={errors}
            />
            <FormInput
              label={t('labels.vatNumber')}
              name="vatNumber"
              type="text"
              register={register}
              errors={errors}
            />
          </>
        )}

        <CountriesDropdown
          setValue={setValue}
          getValues={getValues}
          register={register}
          isFormSubmitted={isFormSubmitted}
        />
        </div>

        {/* Disclaimer - For all user types */}
        <div className="flex-1 min-h-[200px] p-3 text-xs taller:text-sm font-light text-white text-opacity-90 bg-blue bg-opacity-10 border-2 border-blue rounded-xl overflow-y-auto whitespace-pre-line">
          {t('signup.disclaimer')}
        </div>

        <div className="flex-shrink-0 space-y-2">
        <FormCheckbox
          id="accept1"
          checked={accept1}
          setChecked={setAccept1}
          isFormSubmitted={isFormSubmitted}
          label={t('signup.checkbox.acceptDisclaimer')}
        />

        <div className="flex justify-between pt-2">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 border border-white/30 rounded-lg text-white hover:bg-white/10 transition-colors"
          >
            ← {t('signup.step3.back')}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={clsx(
              'px-6 py-3 border border-white/30 rounded-lg text-white transition-colors',
              {
                'hover:bg-white/10': !isSubmitting,
                'opacity-60 cursor-not-allowed': isSubmitting,
              }
            )}
          >
            {t('signup.step3.submit')}
          </button>
        </div>
        </div>
      </form>
    </div>
  )
}

export default Step3PersonalInfo
